import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileArchive, Package } from 'lucide-react';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { snapshotsApi } from '../services/annotatorApi';
import type { SplitSummary } from '../types/api';
import SplitChip from './SplitChip';
import SplitDetailsModal from './SplitDetailsModal';

/**
 * ExportDatasetModal — G.7 redesign (2026-06-07).
 *
 * Customer picks a format (IGNODE Archive / YOLO / COCO) then a
 * container (TAR / ZIP); the download URL is composed with both query
 * params and surfaced via the standard browser-redirect handoff that
 * the caller's onConfirm wires up. Same module flow as before — only
 * the modal contents changed.
 *
 * Defaults: format=IGNODE Archive, container=TAR. Most customers want
 * the IGNODE Archive in TAR; the radios make YOLO + COCO discoverable
 * without pushing them.
 *
 * Per-format archive content recap (kept visible so customers can
 * pick before downloading instead of after):
 *   IGNODE Archive — flat layout with manifest.json carrying split
 *                    metadata. Re-imports back into IGNODE without
 *                    conversion.
 *   YOLO           — Ultralytics layout (data.yaml + images/{train,
 *                    val,test}/ + labels/{train,val,test}/). Drops
 *                    into YOLOv5 / v8 / YOLOX or Roboflow without
 *                    conversion.
 *   COCO           — MS-COCO 2017 (annotations/instances_*.json +
 *                    flat images/). Drops into Detectron2 /
 *                    MMDetection / pycocotools.
 *
 * G.6 — the dataset split summary block stays. Same chips + "View
 * details" → SplitDetailsModal pattern as the canvas-area locked
 * banner.
 */
type Format = 'ignode' | 'yolo' | 'coco';
type Container = 'tar' | 'zip';

interface ExportDatasetModalProps {
    open: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    imageCount: number;
    classCount: number;
    /** Caller receives format + container and triggers the actual
     *  download (typically window.location = snapshotsApi.downloadUrl).
     *  Modal closes after invocation. */
    onConfirm: (format: Format, container: Container) => void;
    /** G.6 — when set, the modal lazy-fetches the split summary on
     *  open so the inline chips render. */
    snapshotId?: string | null;
    orgId?: string;
    /** Pre-sanitized project name for the filename preview. Empty
     *  string falls back to "dataset". */
    projectName?: string;
}

const FORMAT_LABEL: Record<Format, string> = {
    ignode: 'IGNODE Archive',
    yolo: 'YOLO (Ultralytics)',
    coco: 'COCO (2017)',
};

const FORMAT_SUB: Record<Format, string> = {
    ignode:
        'Flat layout with manifest.json. Re-imports back into IGNODE without conversion.',
    yolo:
        'Ultralytics layout (data.yaml + images/{train,val,test}/ + labels/{train,val,test}/). Drops into YOLOv5 / v8 / YOLOX or Roboflow.',
    coco:
        'MS-COCO 2017 (annotations/instances_*.json + flat images/). Drops into Detectron2, MMDetection, or pycocotools.',
};

const CONTAINER_SUB: Record<Container, string> = {
    tar: 'Smaller, no compression overhead. Recommended.',
    zip: 'Ultralytics CLI / Roboflow compatibility.',
};

export default function ExportDatasetModal({
    open,
    onClose,
    isDarkMode = false,
    imageCount,
    classCount,
    onConfirm,
    snapshotId,
    orgId,
    projectName,
}: ExportDatasetModalProps) {
    const { t } = useTranslation('canvas');
    const { t: tCommon } = useTranslation('common');

    const [format, setFormat] = useState<Format>('ignode');
    const [container, setContainer] = useState<Container>('tar');

    // G.6 — split summary lazy-fetch on first open. Cleared on close so
    // a re-lock + re-open picks up fresh data.
    const [summary, setSummary] = useState<SplitSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setSummary(null);
            setDetailsOpen(false);
            // Reset to defaults each time the modal opens so the
            // customer always sees the recommended starting point.
            setFormat('ignode');
            setContainer('tar');
            return;
        }
        if (!snapshotId || !orgId || summary) return;
        let cancelled = false;
        setSummaryLoading(true);
        snapshotsApi
            .splitSummary(snapshotId, orgId)
            .then((data) => {
                if (cancelled) return;
                setSummary(data);
            })
            .catch(() => {
                // Silent — modal still functional without summary.
            })
            .finally(() => {
                if (cancelled) return;
                setSummaryLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, snapshotId, orgId, summary]);

    const subText = isDarkMode ? 'text-gray-400' : 'text-gray-600';
    const sectionLabel = cn(
        'text-[11px] font-semibold uppercase tracking-wider',
        isDarkMode ? 'text-gray-400' : 'text-gray-500',
    );

    // Filename preview — same logic the backend uses. Customer sees
    // the exact filename they'll receive on click.
    const safeName = (projectName ?? '').replace(/[^A-Za-z0-9._-]+/g, '-') || 'dataset';
    const previewFilename = `${safeName}.${format}.${container}`;

    const archiveTreePreview = useMemo(() => archiveTreeFor(format), [format]);

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                title={t('export_dataset_title', { defaultValue: 'Export dataset' })}
                widthClass="max-w-lg"
                isDarkMode={isDarkMode}
                footer={
                    <>
                        <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose}>
                            {tCommon('cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            leftIcon={<Download className="h-4 w-4" />}
                            onClick={() => { onConfirm(format, container); onClose(); }}
                        >
                            {t('export_confirm_named', {
                                defaultValue: 'Download {{filename}}',
                                filename: previewFilename,
                            })}
                        </Button>
                    </>
                }
            >
                <p className={cn('text-sm', isDarkMode ? 'text-gray-300' : 'text-gray-700')}>
                    {t('export_intro_2', {
                        defaultValue:
                            'You\'re about to download {{imageCount}} images with their box annotations. Pick the format and container that matches your downstream tool.',
                        imageCount,
                        classCount,
                    })}
                </p>

                {/* ──────────── 1. Format radios ──────────── */}
                <div className="mt-4 space-y-2">
                    <p className={sectionLabel}>
                        {t('export_format_section', { defaultValue: 'Format' })}
                    </p>
                    <div className="space-y-2">
                        {(['ignode', 'yolo', 'coco'] as const).map((f) => (
                            <RadioCard
                                key={f}
                                isDarkMode={isDarkMode}
                                checked={format === f}
                                onSelect={() => setFormat(f)}
                                name="format"
                                title={FORMAT_LABEL[f]}
                                sub={FORMAT_SUB[f]}
                                icon={<FileArchive className="h-4 w-4" />}
                            />
                        ))}
                    </div>
                </div>

                {/* ──────────── 2. Container radios ──────────── */}
                <div className="mt-5 space-y-2">
                    <p className={sectionLabel}>
                        {t('export_container_section', { defaultValue: 'Container' })}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        {(['tar', 'zip'] as const).map((c) => (
                            <RadioCard
                                key={c}
                                isDarkMode={isDarkMode}
                                checked={container === c}
                                onSelect={() => setContainer(c)}
                                name="container"
                                title={`.${c}`}
                                sub={CONTAINER_SUB[c]}
                                icon={<Package className="h-4 w-4" />}
                            />
                        ))}
                    </div>
                </div>

                {/* ──────────── 3. Dataset split summary ──────────── */}
                {summary && (
                    <div className={cn(
                        'mt-5 rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap',
                        isDarkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50/60',
                    )}>
                        <div>
                            <p className={sectionLabel}>
                                {t('export_split_summary', { defaultValue: 'Dataset split' })}
                            </p>
                            <div className="mt-1 inline-flex items-center gap-2 flex-wrap text-sm">
                                <span className="inline-flex items-center gap-1">
                                    <SplitChip split="train" size="xs" isDarkMode={isDarkMode} />
                                    <span className={cn('tabular-nums', isDarkMode ? 'text-gray-200' : 'text-gray-800')}>
                                        {summary.global.train}
                                    </span>
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <SplitChip split="val" size="xs" isDarkMode={isDarkMode} />
                                    <span className={cn('tabular-nums', isDarkMode ? 'text-gray-200' : 'text-gray-800')}>
                                        {summary.global.val}
                                    </span>
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <SplitChip split="test" size="xs" isDarkMode={isDarkMode} />
                                    <span className={cn('tabular-nums', isDarkMode ? 'text-gray-200' : 'text-gray-800')}>
                                        {summary.global.test}
                                    </span>
                                </span>
                                <span className={cn('text-xs', subText)}>
                                    ({summary.strategy === 'stratified' ? 'stratified' : 'random'}, seed {summary.seed})
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setDetailsOpen(true)}
                            className={cn(
                                'text-xs underline-offset-2 hover:underline shrink-0',
                                isDarkMode ? 'text-primary-300 hover:text-primary-200' : 'text-primary-700 hover:text-primary-900',
                            )}
                        >
                            {t('export_view_split_details', { defaultValue: 'View details' })}
                        </button>
                    </div>
                )}
                {summaryLoading && !summary && (
                    <div className={cn('mt-4 text-xs', subText)}>
                        {t('export_split_loading', { defaultValue: 'Loading dataset split…' })}
                    </div>
                )}

                {/* ──────────── 4. Archive structure preview ──────────── */}
                <div className="mt-5">
                    <p className={cn(sectionLabel, 'mb-1.5')}>
                        {t('export_archive_structure', { defaultValue: 'Archive structure' })}
                    </p>
                    <pre className={cn(
                        'rounded-lg border px-4 py-3 text-[11px] font-mono leading-relaxed overflow-x-auto',
                        isDarkMode ? 'border-gray-700 bg-gray-900 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700',
                    )}>
                        {archiveTreePreview}
                    </pre>
                </div>

                <p className={cn('mt-3 text-xs', subText)}>
                    {t('export_footer', {
                        defaultValue: 'Unlocking the project will delete this snapshot.',
                    })}
                </p>
            </Modal>
            <SplitDetailsModal
                open={detailsOpen}
                summary={summary}
                isDarkMode={isDarkMode}
                onClose={() => setDetailsOpen(false)}
            />
        </>
    );
}

interface RadioCardProps {
    isDarkMode: boolean;
    checked: boolean;
    onSelect: () => void;
    name: string;
    title: string;
    sub: string;
    icon: React.ReactNode;
}

/**
 * RadioCard — reusable selectable card for format + container picks.
 * Whole row is clickable; the actual radio input lives off-screen for
 * accessibility (keyboard nav + screen readers still see it as a
 * radio group). The visible card flips border + background when
 * selected.
 */
function RadioCard({ isDarkMode, checked, onSelect, name, title, sub, icon }: RadioCardProps) {
    return (
        <label
            className={cn(
                'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                checked
                    ? isDarkMode
                        ? 'border-primary-500/60 bg-primary-500/10'
                        : 'border-primary-400 bg-primary-50'
                    : isDarkMode
                        ? 'border-gray-700 bg-gray-800/30 hover:bg-gray-800/60'
                        : 'border-gray-200 bg-white hover:bg-gray-50',
            )}
        >
            <input
                type="radio"
                name={name}
                checked={checked}
                onChange={onSelect}
                className="mt-1 cursor-pointer"
            />
            <span className={cn(
                'h-8 w-8 shrink-0 rounded-md flex items-center justify-center mt-0.5',
                checked
                    ? isDarkMode ? 'bg-primary-500/20 text-primary-200' : 'bg-primary-100 text-primary-700'
                    : isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-500',
            )}>
                {icon}
            </span>
            <span className="min-w-0 flex-1">
                <span className={cn(
                    'block text-sm font-medium',
                    isDarkMode ? 'text-gray-100' : 'text-gray-900',
                )}>
                    {title}
                </span>
                <span className={cn(
                    'block text-xs mt-0.5',
                    isDarkMode ? 'text-gray-400' : 'text-gray-600',
                )}>
                    {sub}
                </span>
            </span>
        </label>
    );
}

/**
 * archiveTreeFor returns the pre-formatted ASCII tree the customer
 * sees in the Archive structure preview block. Switches on format so
 * they can predict the directory shape before they click Download.
 */
function archiveTreeFor(format: Format): string {
    switch (format) {
        case 'ignode':
            return `dataset.<ext>
├── images/
│   ├── <image_uuid>.<ext>
│   └── ...
├── labels/
│   ├── <image_uuid>.txt        ← YOLO format
│   └── ...
├── classes.txt
└── manifest.json               ← IGNODE Archive v1.0 (split block)`;
        case 'yolo':
            return `dataset.<ext>
├── data.yaml                   ← class names + split paths
├── images/
│   ├── train/<uuid>.<ext>
│   ├── val/<uuid>.<ext>
│   └── test/<uuid>.<ext>
└── labels/
    ├── train/<uuid>.txt        ← YOLO format
    ├── val/<uuid>.txt
    └── test/<uuid>.txt`;
        case 'coco':
            return `dataset.<ext>
├── annotations/
│   ├── instances_train.json    ← MS-COCO 2017 schema
│   ├── instances_val.json
│   └── instances_test.json
└── images/
    ├── train/<uuid>.<ext>
    ├── val/<uuid>.<ext>
    └── test/<uuid>.<ext>`;
    }
}
